import React, { useState, useEffect } from 'react';
import companyService from '../../services/companyService';
import adminService, { CompanyAdmin } from '../../services/adminService';
import InputField from '../../components/common/InputField/InputField';
import styles from './CompanyManagement.module.scss';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface Company {
  id: number;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  website: string;
  logo_url: string;
  subdomain?: string;
  is_active: boolean;
  storage_enabled?: boolean;
  is_cloud_active?: boolean;
  created_at: string;
  updated_at: string;
}

interface CompanyStats {
  total_employees: number;
  total_customers: number;
  total_invoices: number;
  active_invoices: number;
}

const CompanyManagement: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<CompanyStats | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [adminCredentials, setAdminCredentials] = useState<{email: string, password: string, subdomain?: string} | null>(null);
  const [resetPasswordModal, setResetPasswordModal] = useState(false);
  const [selectedCompanyForReset, setSelectedCompanyForReset] = useState<Company | null>(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordResult, setResetPasswordResult] = useState<{company_name: string, admin_name: string, admin_email: string, new_password: string} | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    website: '',
    logo_url: '',
    is_active: true,
    storage_enabled: true,
    is_cloud_active: false,
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const companiesData = await companyService.getCompanies();
      setCompanies(companiesData);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyStats = async (companyId: number) => {
    try {
      const statsData = await companyService.getCompanyStats(companyId);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching company stats:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (isEditMode && selectedCompany) {
        await companyService.updateCompany({
          id: selectedCompany.id,
          ...formData
        });
      } else {
        const response = await companyService.createCompany(formData);
        
        // Show admin credentials if company was created successfully
        if (response.admin) {
          setAdminCredentials({
            email: response.admin.email,
            password: response.admin.password,
            subdomain: response.subdomain
          });
        }
      }
      setShowModal(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        country: '',
        website: '',
        logo_url: '',
        is_active: true,
        storage_enabled: true,
        is_cloud_active: false
      });
      fetchCompanies();
    } catch (error) {
      console.error('Error saving company:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (company: Company) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name,
      email: company.email || '',
      phone: company.phone || '',
      address: company.address || '',
      city: company.city || '',
      state: company.state || '',
      zip_code: company.zip_code || '',
      country: company.country || '',
      website: company.website || '',
      logo_url: company.logo_url || '',
      is_active: company.is_active,
      storage_enabled: company.storage_enabled ?? true,
      is_cloud_active: company.is_cloud_active ?? false
    });
    setIsEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (companyId: number) => {
    const company = companies.find(c => c.id === companyId);
    if (company) {
      setCompanyToDelete(company);
      setDeleteModalOpen(true);
    }
  };

  const confirmDelete = async () => {
    if (companyToDelete) {
      try {
        setDeleting(true);
        await companyService.deleteCompany(companyToDelete.id);
        fetchCompanies();
        setDeleteModalOpen(false);
        setCompanyToDelete(null);
      } catch (error) {
        console.error('Error deleting company:', error);
      } finally {
        setDeleting(false);
      }
    }
  };

  const handleToggleStatus = async (companyId: number, currentStatus: boolean) => {
    try {
      await companyService.toggleCompanyStatus(companyId, !currentStatus);
      fetchCompanies();
    } catch (error) {
      console.error('Error toggling company status:', error);
    }
  };

  const handleResetPassword = async (company: Company) => {
    setSelectedCompanyForReset(company);
    setResetPasswordModal(true);
  };

  const confirmResetPassword = async () => {
    if (!selectedCompanyForReset) return;
    
    try {
      setResetPasswordLoading(true);
      const result = await adminService.resetAdminPassword(selectedCompanyForReset.id);
      setResetPasswordResult({
        company_name: result.company_name,
        admin_name: result.admin_name,
        admin_email: result.admin_email,
        new_password: result.new_password
      });
      toast.success('Admin password reset successfully!');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Failed to reset admin password');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const closeResetPasswordModal = () => {
    setResetPasswordModal(false);
    setSelectedCompanyForReset(null);
    setResetPasswordResult(null);
  };

  const openCreateModal = () => {
    setSelectedCompany(null);
    setIsEditMode(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: '',
      website: '',
      logo_url: '',
      is_active: true,
      storage_enabled: true,
      is_cloud_active: false
    });
    setShowModal(true);
  };

  const openStatsModal = async (company: Company) => {
    setSelectedCompany(company);
    await fetchCompanyStats(company.id);
    setShowModal(true);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Company Management</h1>
        <button onClick={openCreateModal} className={`${styles.button} ${styles.primaryButton}`}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add New Company
        </button>
      </div>

      {loading ? (
        <div className={styles.loading}>Loading...</div>
      ) : (
        <div className={styles.companiesGrid}>
          {companies.map((company) => (
            <div key={company.id} className={styles.companyCard}>
              <div className={styles.companyHeader}>
                <h3>{company.name}</h3>
                <div className={`${styles.statusBadge} ${company.is_active ? styles.activeBadge : styles.inactiveBadge}`}>
                  {company.is_active ? 'Active' : 'Inactive'}
                </div>
              </div>
              
              <div className={styles.companyInfo}>
                {company.email && <p><strong>Email:</strong> {company.email}</p>}
                {company.phone && <p><strong>Phone:</strong> {company.phone}</p>}
                {company.city && <p><strong>City:</strong> {company.city}</p>}
                {company.country && <p><strong>Country:</strong> {company.country}</p>}
                {company.subdomain && <p><strong>Subdomain:</strong> {company.subdomain}</p>}
                <p><strong>Storage:</strong> {company.storage_enabled ? 'Enabled' : 'Disabled'}</p>
                <p><strong>Created:</strong> {new Date(company.created_at).toLocaleDateString()}</p>
              </div>

              <div className={styles.companyActions}>
                <button 
                  onClick={() => handleEdit(company)}
                  className={`${styles.button} ${styles.iconButton} ${styles.editButton}`}
                  title="Edit Company"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                    <path d="m15 5 4 4"/>
                  </svg>
                </button>
                <button 
                  onClick={() => handleResetPassword(company)}
                  className={`${styles.button} ${styles.iconButton} ${styles.warningButton}`}
                  title="Reset Admin Password"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
                    <path d="m7 11V7a5 5 0 0 1 10 0v4"/>
                    <circle cx="12" cy="16" r="1"/>
                  </svg>
                </button>
                <button 
                  onClick={() => handleToggleStatus(company.id, company.is_active)}
                  className={`${styles.button} ${styles.iconButton} ${company.is_active ? styles.warningButton : styles.successButton}`}
                  title={company.is_active ? 'Deactivate Company' : 'Activate Company'}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    {company.is_active ? (
                      <>
                        <circle cx="12" cy="12" r="10"/>
                        <path d="m15 9-6 6"/>
                        <path d="m9 9 6 6"/>
                      </>
                    ) : (
                      <>
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M8 12l2 2 4-4"/>
                      </>
                    )}
                  </svg>
                </button>
                <button 
                  onClick={() => handleDelete(company.id)}
                  className={`${styles.button} ${styles.iconButton} ${styles.dangerButton}`}
                  title="Delete Company"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18"/>
                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                    <line x1="10" y1="11" x2="10" y2="17"/>
                    <line x1="14" y1="11" x2="14" y2="17"/>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>{isEditMode ? 'Edit Company' : 'Add New Company'}</h2>
              <button 
                onClick={() => setShowModal(false)}
                className={styles.closeButton}
              >
                ×
              </button>
            </div>

            {selectedCompany && stats ? (
              <div className={styles.statsContent}>
                <h3>Company Statistics</h3>
                <div className={styles.statsGrid}>
                  <div className={styles.statItem}>
                    <span className={styles.statNumber}>{stats.total_employees}</span>
                    <span className={styles.statLabel}>Employees</span>
                  </div>
                  <div className={styles.statItem}>
                    <span className={styles.statNumber}>{stats.total_customers}</span>
                    <span className={styles.statLabel}>Customers</span>
                  </div>
                  <div className={styles.statItem}>
                    <span className={styles.statNumber}>{stats.total_invoices}</span>
                    <span className={styles.statLabel}>Total Invoices</span>
                  </div>
                  <div className={styles.statItem}>
                    <span className={styles.statNumber}>{stats.active_invoices}</span>
                    <span className={styles.statLabel}>Active Invoices</span>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <InputField
                      label="Company Name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      placeholder="Enter company name"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <InputField
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="Enter company email"
                      required
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <InputField
                      label="Phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <InputField
                      label="Website"
                      name="website"
                      type="text"
                      value={formData.website}
                      onChange={(e) => setFormData({...formData, website: e.target.value})}
                      placeholder="Enter website URL"
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <InputField
                    label="Address"
                    name="address"
                    type="textarea"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="Enter company address"
                    rows={3}
                  />
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <InputField
                      label="City"
                      name="city"
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      placeholder="Enter city"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <InputField
                      label="State"
                      name="state"
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      placeholder="Enter state"
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <InputField
                      label="ZIP Code"
                      name="zip_code"
                      type="text"
                      value={formData.zip_code}
                      onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                      placeholder="Enter ZIP code"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <InputField
                      label="Country"
                      name="country"
                      type="text"
                      value={formData.country}
                      onChange={(e) => setFormData({...formData, country: e.target.value})}
                      placeholder="Enter country"
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <InputField
                    label="Logo URL"
                    name="logo_url"
                    type="text"
                    value={formData.logo_url}
                    onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                    placeholder="Enter logo URL"
                  />
                </div>

                <div className={styles.checkboxGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    />
                    <span className={styles.checkmark}></span>
                    Active
                  </label>
                </div>

                <div className={styles.checkboxGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.storage_enabled}
                      onChange={(e) => setFormData({...formData, storage_enabled: e.target.checked})}
                    />
                    <span className={styles.checkmark}></span>
                    Storage Enabled
                  </label>
                </div>

                <div className={styles.checkboxGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.is_cloud_active}
                      onChange={(e) => setFormData({...formData, is_cloud_active: e.target.checked})}
                    />
                    <span className={styles.checkmark}></span>
                    Cloud Storage Enabled
                  </label>
                </div>

                <div className={styles.formActions}>
                  <button 
                    type="button" 
                    onClick={() => setShowModal(false)}
                    className={`${styles.button} ${styles.secondaryButton}`}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    disabled={loading}
                    className={`${styles.button} ${styles.primaryButton}`}
                  >
                    {loading ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Delete Company</h2>
              <button 
                onClick={() => {
                  setDeleteModalOpen(false);
                  setCompanyToDelete(null);
                }}
                className={styles.closeButton}
              >
                ×
              </button>
            </div>
            <div className={styles.modalContent}>
              <p>Are you sure you want to delete <strong>{companyToDelete?.name}</strong>?</p>
              <p>This action cannot be undone.</p>
              <div className={styles.modalActions}>
                <button 
                  type="button" 
                  onClick={() => {
                    setDeleteModalOpen(false);
                    setCompanyToDelete(null);
                  }}
                  className={`${styles.button} ${styles.secondaryButton}`}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  onClick={confirmDelete}
                  disabled={deleting}
                  className={`${styles.button} ${styles.dangerButton}`}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Credentials Modal */}
      {adminCredentials && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Company Created Successfully!</h2>
              <button 
                onClick={() => setAdminCredentials(null)}
                className={styles.closeButton}
              >
                ×
              </button>
            </div>
            <div className={styles.modalContent}>
              <p>Company has been created successfully. Here are the admin credentials:</p>
              <div className={styles.credentialsBox}>
                <div className={styles.credentialItem}>
                  <strong>Email:</strong> {adminCredentials.email}
                </div>
                <div className={styles.credentialItem}>
                  <strong>Password:</strong> {adminCredentials.password}
                </div>
                {adminCredentials.subdomain && (
                  <div className={styles.credentialItem}>
                    <strong>Subdomain:</strong> {adminCredentials.subdomain}
                  </div>
                )}
              </div>
              <p style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
                Please share these credentials with the company admin.
              </p>
              <div className={styles.modalActions}>
                <button 
                  type="button" 
                  onClick={() => setAdminCredentials(null)}
                  className={`${styles.button} ${styles.primaryButton}`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Reset Admin Password</h2>
              <button 
                onClick={closeResetPasswordModal}
                className={styles.closeButton}
              >
                ×
              </button>
            </div>
            <div className={styles.modalContent}>
              {!resetPasswordResult ? (
                <>
                  <p>Are you sure you want to reset the admin password for <strong>{selectedCompanyForReset?.name}</strong>?</p>
                  <p>The new password will be: <strong>Admin@123</strong></p>
                  <div className={styles.modalActions}>
                    <button 
                      type="button" 
                      onClick={closeResetPasswordModal}
                      className={`${styles.button} ${styles.secondaryButton}`}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button" 
                      onClick={confirmResetPassword}
                      disabled={resetPasswordLoading}
                      className={`${styles.button} ${styles.warningButton}`}
                    >
                      {resetPasswordLoading ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p>Admin password has been reset successfully!</p>
                  <div className={styles.credentialsBox}>
                    <div className={styles.credentialItem}>
                      <strong>Company:</strong> {resetPasswordResult.company_name}
                    </div>
                    <div className={styles.credentialItem}>
                      <strong>Admin Name:</strong> {resetPasswordResult.admin_name}
                    </div>
                    <div className={styles.credentialItem}>
                      <strong>Admin Email:</strong> {resetPasswordResult.admin_email}
                    </div>
                    <div className={styles.credentialItem}>
                      <strong>New Password:</strong> {resetPasswordResult.new_password}
                    </div>
                  </div>
                  <p style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
                    Please share the new password with the company admin.
                  </p>
                  <div className={styles.modalActions}>
                    <button 
                      type="button" 
                      onClick={closeResetPasswordModal}
                      className={`${styles.button} ${styles.primaryButton}`}
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default CompanyManagement;